import React, { useState } from "react";
import api from "@/lib/api";
import { formatPrice } from "@/components/ProductCard";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, X } from "@phosphor-icons/react";

const inputCls = "w-full border border-black/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";

export const CFDI_USES = [
  ["G01", "G01 · Adquisición de mercancías"],
  ["G03", "G03 · Gastos en general"],
  ["P01", "P01 · Por definir"],
  ["D01", "D01 · Honorarios médicos y gastos hospitalarios"],
  ["I08", "I08 · Otra maquinaria y equipo"],
  ["S01", "S01 · Sin efectos fiscales"],
];

export const TAX_REGIMES = [
  ["601", "601 · General de Ley Personas Morales"],
  ["603", "603 · Personas Morales con Fines no Lucrativos"],
  ["605", "605 · Sueldos y Salarios e Ingresos Asimilados"],
  ["612", "612 · Personas Físicas con Actividades Empresariales"],
  ["616", "616 · Sin obligaciones fiscales"],
  ["621", "621 · Incorporación Fiscal"],
  ["626", "626 · Régimen Simplificado de Confianza (RESICO)"],
];

const blank = { rfc: "", business_name: "", tax_regime: "", cfdi_use: "", zip: "", email: "" };

export default function InvoiceModal({ order, savedFiscal, onClose, onDone }) {
  const hasSaved = savedFiscal && savedFiscal.rfc;
  const [form, setForm] = useState(hasSaved ? { ...blank, ...savedFiscal } : { ...blank });
  const [saveDefault, setSaveDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.rfc.trim() || !form.business_name.trim()) { toast.error("RFC y nombre/razón social son obligatorios"); return; }
    if (!form.tax_regime || !form.cfdi_use || !form.zip.trim()) { toast.error("Completa régimen fiscal, uso de CFDI y código postal"); return; }
    setSaving(true);
    try {
      await api.post(`/buyer/orders/${order.id}/request-invoice`, { fiscal: form, save_default: saveDefault });
      toast.success("Factura solicitada. Te avisaremos cuando esté lista.");
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo solicitar la factura"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-foreground/40 flex items-start justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-lg nb-shadow-lg w-full max-w-lg my-8" data-testid="invoice-modal">
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <h2 className="text-lg font-extrabold tracking-tight flex items-center gap-2"><Receipt weight="bold" size={20} className="text-primary" /> Solicitar factura</h2>
          <button onClick={onClose} className="nb-btn bg-secondary p-1.5"><X weight="bold" size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-secondary/40 rounded-md p-3 text-sm">
            <p className="font-semibold">Pedido {order.order_number || order.id.slice(0, 8)}</p>
            <p className="text-xs text-muted-foreground">{order.items?.length} producto(s) · {formatPrice(order.total)}</p>
          </div>
          {hasSaved && (
            <button type="button" onClick={() => setForm({ ...blank, ...savedFiscal })} data-testid="use-saved-fiscal"
                    className="text-xs text-primary font-semibold hover:underline">Usar mis datos fiscales guardados</button>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide">RFC</label>
              <input value={form.rfc} onChange={(e) => set("rfc", e.target.value.toUpperCase())} data-testid="fiscal-rfc" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide">Código postal</label>
              <input value={form.zip} onChange={(e) => set("zip", e.target.value)} data-testid="fiscal-zip" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Nombre / Razón social</label>
            <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="fiscal-name" className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Régimen fiscal</label>
            <Select value={form.tax_regime} onValueChange={(v) => set("tax_regime", v)}>
              <SelectTrigger data-testid="fiscal-regime" className="rounded-md text-sm mt-1"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent className="rounded-md">{TAX_REGIMES.map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Uso de CFDI</label>
            <Select value={form.cfdi_use} onValueChange={(v) => set("cfdi_use", v)}>
              <SelectTrigger data-testid="fiscal-cfdi" className="rounded-md text-sm mt-1"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent className="rounded-md">{CFDI_USES.map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Correo para la factura</label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="fiscal-email" className={inputCls} />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} data-testid="fiscal-save-default" />
            Guardar como mis datos fiscales predeterminados
          </label>
          <button onClick={submit} disabled={saving} data-testid="submit-invoice-request" className="w-full nb-btn bg-primary text-primary-foreground py-2.5 hover:brightness-110 disabled:opacity-50">{saving ? "Enviando…" : "Solicitar factura"}</button>
        </div>
      </div>
    </div>
  );
}
